import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Alert,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import Visible from "components/common/Visible";
import * as naicsAutoSelect from "components/inputs/program/naicsAutoSelect";
import GLClassificationsTable from "components/pricing/glPricing/GLClassificationsTable";
import GlPricingResults from "components/pricing/glPricing/GlPricingResults";
import ProjectWrapInputs from "components/pricing/glPricing/ProjectWrapInputs";
import { GL_PRICING_CLASSIFICATIONS } from "flags";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import { updateLayer } from "store/actions/pricing/pricingActions";
import * as baseLayerBurnStore from "store/baseLayerBurn";
import * as glExposuresStore from "store/glExposures";
import * as flagsSelectors from "store/selectors/input/flags/flagsSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const FONT_SIZE = "0.875rem";

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

// Coverage Option dropdown values for the Deductible section. Mirrors the
// revised "Deductible Liability Insurance Including Supplementary Payments"
// schedule the customer sent on 2026-05-25. Values are stored verbatim so
// they can flow into the docx template's Schedule table without a separate
// display-label map. If another customer adopts this dropdown later, move
// this list to a static-data config and read via useConfigQuery.
const COVERAGE_OPTIONS = [
  "Bodily Injury Liability (Coverage A)",
  "Property Damage Liability (Coverage A)",
  "Bodily Injury and/or Property Damage Liability Combined (Coverage A)",
  "Bodily Injury and/or Property Damage Liability and Personal and Advertising Injury Liability Combined (Coverages A and B)",
];

// Dropdown values for the GL Pricing limit/deductible fields (client request,
// 2026-07-31). Dollar options are stored as numbers; the sentinel strings
// ("Not Covered"/"Excluded"/"Included") are stored verbatim on the layer — the
// submission resolver maps them to null for the engine without falling back to
// the flag-derived value (an explicit "Excluded" must not resurrect a flag).
const LIQUOR_LIMIT_OPTIONS = [500_000, 1_000_000, 2_000_000, "Not Covered"];
const DEDUCTIBLE_AMOUNT_OPTIONS = [
  0,
  250,
  500,
  750,
  1_000,
  2_000,
  3_000,
  4_000,
  5_000,
  10_000,
  15_000,
  20_000,
  25_000,
  50_000,
  75_000,
  100_000,
];
const STOP_GAP_LIMIT_OPTIONS = [500_000, 1_000_000, "Not Covered"];
const OCCURRENCE_LIMIT_OPTIONS = [1_000_000, 2_000_000];
const AGGREGATE_OPTIONS = [
  1_000_000,
  2_000_000,
  3_000_000,
  4_000_000,
  "Excluded",
];
const DAMAGE_TO_PREMISES_OPTIONS = [
  50_000,
  100_000,
  200_000,
  250_000,
  300_000,
  500_000,
  1_000_000,
  "Excluded",
];
const MEDICAL_EXPENSE_OPTIONS = [
  1_000,
  2_000,
  2_500,
  5_000,
  10_000,
  15_000,
  20_000,
  25_000,
  "Excluded",
];
const PAI_LIMIT_OPTIONS = [1_000_000, 2_000_000, "Excluded"];
const EMPLOYEE_BENEFITS_LIMIT_OPTIONS = [1_000_000, 2_000_000, "Not Covered"];
const ASSAULT_BATTERY_EACH_EVENT_OPTIONS = [
  25_000,
  50_000,
  100_000,
  200_000,
  250_000,
  300_000,
  500_000,
  1_000_000,
  "Excluded",
  "Included",
];
const ASSAULT_BATTERY_AGGREGATE_OPTIONS = [
  25_000,
  50_000,
  100_000,
  200_000,
  250_000,
  300_000,
  500_000,
  1_000_000,
  2_000_000,
  "Excluded",
  "Included",
];
const HNOA_LIMIT_OPTIONS = [1_000_000, "Not Covered", "Excluded"];

const fmtLimitOption = (o) =>
  typeof o === "number" ? `$${o.toLocaleString()}` : o;

// Limit dropdown: numeric options display as $X,XXX and store as numbers;
// sentinel strings store verbatim. A stored value not in the option list (a
// legacy free-text entry) is prepended so it still displays.
const LimitSelectField = ({ label, value, options, onChange }) => {
  const opts =
    value != null && value !== "" && !options.includes(value)
      ? [value, ...options]
      : options;
  return (
    <TextField
      {...TF}
      select
      label={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
    >
      <MenuItem value={""}>{"—"}</MenuItem>
      {opts.map((o) => (
        <MenuItem key={o} value={o}>
          {fmtLimitOption(o)}
        </MenuItem>
      ))}
    </TextField>
  );
};

const IRPM_LINES = [
  {
    key: "irpmConditionCare",
    label: "Condition and care of premises",
    cap: 0.05,
  },
  {
    key: "irpmTypeCondition",
    label: "Type, condition, and care of equipment",
    cap: 0.05,
  },
  {
    key: "irpmSelectionTraining",
    label: "Selection, training, supervision, and experience",
    cap: 0.05,
  },
  { key: "irpmSafetyProgram", label: "Safety program", cap: 0.02 },
  {
    key: "irpmPeculiarities",
    label: "Peculiarities of classification",
    cap: 0.1,
  },
  { key: "irpmTermsConditions", label: "Terms and conditions", cap: 0.1 },
  {
    key: "irpmUniqueRisk",
    label: "Unique risk not otherwise contemplated",
    cap: null,
  },
];

const IRPM_MIN_MODIFIER = -0.5;

const SectionBar = ({ label, summary, summaryColor, open, onToggle }) => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    justifyContent={"space-between"}
    onClick={onToggle}
    sx={{
      backgroundColor: "grey.100",
      cursor: "pointer",
      px: 2,
      py: 1.25,
      borderRadius: 1,
    }}
  >
    <Typography fontSize={FONT_SIZE} fontWeight={700} color={"text.secondary"}>
      {label}
    </Typography>
    <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
      {summary != null && (
        <Typography
          fontSize={FONT_SIZE}
          fontWeight={600}
          color={summaryColor ?? "text.secondary"}
        >
          {summary}
        </Typography>
      )}
      {open ? (
        <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      )}
    </Stack>
  </Stack>
);

const SubSectionTitle = ({ children }) => (
  <Typography
    variant={"overline"}
    color={"text.secondary"}
    sx={{ letterSpacing: 1, lineHeight: 1, fontSize: "0.7rem" }}
  >
    {children}
  </Typography>
);

const IrpmLine = ({ line, layer, onUpdate }) => {
  const decimal = layer?.[line.key];
  const display = decimal != null ? decimal * 100 : null;
  const capPct = line.cap != null ? line.cap * 100 : null;
  const isOverCap =
    capPct != null && display != null && Math.abs(display) > capPct + 1e-9;
  return (
    <Grid container alignItems={"center"} spacing={2} sx={{ py: 0.25 }}>
      <Grid item xs={6}>
        <Typography fontSize={"0.825rem"}>{line.label}</Typography>
      </Grid>
      <Grid item xs={4}>
        <PrettyNumberTextField
          {...TF}
          hiddenLabel
          placeholder={"%"}
          error={isOverCap}
          helperText={isOverCap ? `Exceeds cap of ±${capPct}%` : undefined}
          value={display}
          onChangeNumber={(v) =>
            onUpdate({ [line.key]: v != null ? v / 100 : null })
          }
        />
      </Grid>
      <Grid item xs={2}>
        <Typography
          fontSize={"0.75rem"}
          color={isOverCap ? "error.main" : "text.secondary"}
          sx={{ whiteSpace: "nowrap" }}
        >
          {capPct != null ? `Max ±${capPct}%` : "No cap"}
        </Typography>
      </Grid>
    </Grid>
  );
};

const IrpmSection = ({ layer, onUpdate, open, onToggle }) => {
  const totalDecimal = IRPM_LINES.reduce(
    (acc, line) => acc + Number(layer?.[line.key] ?? 0),
    0
  );
  const cappedDecimal = Math.max(IRPM_MIN_MODIFIER, totalDecimal);
  const totalPct = totalDecimal * 100;
  const cappedPct = cappedDecimal * 100;
  const isCapped = totalDecimal < IRPM_MIN_MODIFIER - 1e-9;
  const hasAny = totalDecimal !== 0;

  const summary = hasAny ? `Total: ${cappedPct.toFixed(2)}%` : null;
  const summaryColor = isCapped
    ? "error.main"
    : cappedDecimal < 0
    ? "success.main"
    : "warning.main";

  return (
    <Stack spacing={0}>
      <SectionBar
        label={"Individual Risk Premium Modifications (IRPM)"}
        summary={summary}
        summaryColor={hasAny ? summaryColor : undefined}
        open={open}
        onToggle={onToggle}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Stack spacing={0.5} sx={{ pt: 1.5, pb: 1, px: 1 }}>
          {IRPM_LINES.map((line) => (
            <IrpmLine
              key={line.key}
              line={line}
              layer={layer}
              onUpdate={onUpdate}
            />
          ))}
          <Grid
            container
            alignItems={"center"}
            spacing={2}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 0.5,
              pt: 1.25,
            }}
          >
            <Grid item xs={6}>
              <Typography fontSize={"0.85rem"} fontWeight={600}>
                {"Total Modification"}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                fontSize={"0.95rem"}
                fontWeight={700}
                color={isCapped ? "error.main" : "text.primary"}
              >
                {`${cappedPct.toFixed(2)}%`}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography
                fontSize={"0.7rem"}
                color={isCapped ? "error.main" : "text.secondary"}
              >
                {isCapped
                  ? `Floored from ${totalPct.toFixed(2)}% (max credit −50%)`
                  : "Max credit −50%"}
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
    </Stack>
  );
};

const CalcHint = ({ children }) => (
  <Typography fontSize={"0.75rem"} color={"text.secondary"} sx={{ mt: 0.25 }}>
    {children}
  </Typography>
);

const AdditionalSettings = ({
  layer,
  states,
  lastTotals,
  programState,
  applyExperienceMod,
  experienceMod,
  experienceModQualifies,
  onToggleExperienceMod,
  onUpdate,
}) => {
  const [open, setOpen] = useState(false);
  const [irpmOpen, setIrpmOpen] = useState(false);

  const filledCount = [
    layer?.predominantState,
    layer?.liquorOccurrenceLimit,
    layer?.liquorAggregateLimit,
    layer?.deductibleAmount,
    layer?.coverageEnhancementsPct,
    layer?.stopGapPayroll,
    layer?.hnoaEmployees,
    layer?.hnoaCostOfHire,
    layer?.hnoaLimit,
    layer?.employeeBenefitsEachEmployeeLimit,
    layer?.employeeBenefitsAggregateLimit,
    layer?.assaultBatteryEachEventLimit,
    layer?.assaultBatteryAggregateLimit,
    layer?.manualPremiumAdjustment,
    layer?.triaPercent,
    layer?.commissionPct,
    ...IRPM_LINES.map((l) => layer?.[l.key]),
  ].filter((v) => v != null && v !== 0 && v !== "").length;

  const covEnhPremium =
    lastTotals?.prem_ops_base != null && layer?.coverageEnhancementsPct != null
      ? lastTotals.prem_ops_base * layer.coverageEnhancementsPct
      : null;
  const stopGapPremium =
    layer?.stopGapPayroll != null ? (layer.stopGapPayroll / 1000) * 0.5 : null;
  const hnoaPptEq =
    layer?.hnoaEmployees || layer?.hnoaCostOfHire
      ? (layer?.hnoaCostOfHire ?? 0) / 60 / 260 +
        (layer?.hnoaEmployees ?? 0) * 0.2
      : null;

  const fmt = (v) => (v != null ? `$${Math.round(v).toLocaleString()}` : null);

  return (
    <Stack spacing={0}>
      <SectionBar
        label={"Additional Settings"}
        summary={filledCount > 0 ? `${filledCount} set` : null}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Stack spacing={2} sx={{ pt: 2, pb: 1, px: 1, pr: 1 }}>
          <Stack spacing={0.75}>
            <SubSectionTitle>{"Risk Detail"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  {...TF}
                  select
                  label={"Predominant State"}
                  value={layer?.predominantState ?? programState ?? ""}
                  onChange={(e) =>
                    onUpdate({ predominantState: e.target.value || null })
                  }
                >
                  <MenuItem value={""}>{"—"}</MenuItem>
                  {states.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.label ?? s.code}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Liquor Each Occurrence"}
                  value={layer?.liquorOccurrenceLimit ?? null}
                  options={LIQUOR_LIMIT_OPTIONS}
                  onChange={(liquorOccurrenceLimit) =>
                    onUpdate({ liquorOccurrenceLimit })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Liquor Aggregate"}
                  value={layer?.liquorAggregateLimit ?? null}
                  options={LIQUOR_LIMIT_OPTIONS}
                  onChange={(liquorAggregateLimit) =>
                    onUpdate({ liquorAggregateLimit })
                  }
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Deductible"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Deductible Amount"}
                  value={layer?.deductibleAmount ?? null}
                  options={DEDUCTIBLE_AMOUNT_OPTIONS}
                  onChange={(deductibleAmount) =>
                    onUpdate({ deductibleAmount })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  {...TF}
                  select
                  label={"Deductible Type"}
                  value={layer?.deductibleType ?? "Per Occurrence"}
                  onChange={(e) => onUpdate({ deductibleType: e.target.value })}
                >
                  <MenuItem value={"Per Occurrence"}>
                    {"Per Occurrence"}
                  </MenuItem>
                  <MenuItem value={"Per Claim"}>{"Per Claim"}</MenuItem>
                  <MenuItem value={"No Deductible"}>{"No Deductible"}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  {...TF}
                  select
                  label={"Coverage Option"}
                  value={layer?.coverageOption ?? ""}
                  onChange={(e) =>
                    onUpdate({ coverageOption: e.target.value || null })
                  }
                >
                  <MenuItem value={""}>{"—"}</MenuItem>
                  {COVERAGE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Additional Deductible ($)"}
                  value={layer?.additionalDeductible ?? null}
                  onChangeNumber={(additionalDeductible) =>
                    onUpdate({ additionalDeductible })
                  }
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Additional Premium"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Coverage Enhancements (%)"}
                  value={
                    layer?.coverageEnhancementsPct != null
                      ? layer.coverageEnhancementsPct * 100
                      : null
                  }
                  onChangeNumber={(v) =>
                    onUpdate({
                      coverageEnhancementsPct: v != null ? v / 100 : null,
                    })
                  }
                />
                {covEnhPremium != null && (
                  <CalcHint>{`= ${fmt(covEnhPremium)}`}</CalcHint>
                )}
              </Grid>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Stop Gap Payroll ($)"}
                  value={layer?.stopGapPayroll ?? null}
                  onChangeNumber={(stopGapPayroll) =>
                    onUpdate({ stopGapPayroll })
                  }
                />
                {stopGapPremium != null && stopGapPremium > 0 && (
                  <CalcHint>
                    {`= ${fmt(stopGapPremium)} (payroll / 1,000 × $0.50)`}
                  </CalcHint>
                )}
              </Grid>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Stop Gap Limit"}
                  value={layer?.stopGapLimit ?? null}
                  options={STOP_GAP_LIMIT_OPTIONS}
                  onChange={(stopGapLimit) => onUpdate({ stopGapLimit })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  {...TF}
                  select
                  label={"Renewable"}
                  value={layer?.renewable ?? ""}
                  onChange={(e) =>
                    onUpdate({ renewable: e.target.value || null })
                  }
                >
                  <MenuItem value={""}>{"—"}</MenuItem>
                  <MenuItem value={"Y"}>{"Yes"}</MenuItem>
                  <MenuItem value={"N"}>{"No"}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Hired / Non-Owned Auto"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <PrettyNumberTextField
                  {...TF}
                  label={"No. of Employees"}
                  value={layer?.hnoaEmployees ?? null}
                  onChangeNumber={(hnoaEmployees) =>
                    onUpdate({ hnoaEmployees })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Cost of Hire ($)"}
                  value={layer?.hnoaCostOfHire ?? null}
                  onChangeNumber={(hnoaCostOfHire) =>
                    onUpdate({ hnoaCostOfHire })
                  }
                />
                {hnoaPptEq != null && (
                  <CalcHint>
                    {`PPT equivalent: ${hnoaPptEq.toFixed(2)}`}
                  </CalcHint>
                )}
              </Grid>
              <Grid item xs={3}>
                <LimitSelectField
                  label={"HNOA Limit"}
                  value={layer?.hnoaLimit ?? null}
                  options={HNOA_LIMIT_OPTIONS}
                  onChange={(hnoaLimit) => onUpdate({ hnoaLimit })}
                />
              </Grid>
              <Grid item xs={3}>
                {lastTotals?.hnoa_premium != null && (
                  <Stack spacing={0} sx={{ pt: 0.5 }}>
                    <Typography fontSize={"0.75rem"} color={"text.secondary"}>
                      {"HNOA Premium"}
                    </Typography>
                    <Typography fontSize={"0.875rem"} fontWeight={600}>
                      {fmt(lastTotals.hnoa_premium)}
                    </Typography>
                  </Stack>
                )}
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Employee Benefits"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Each Employee Limit"}
                  value={layer?.employeeBenefitsEachEmployeeLimit ?? null}
                  options={EMPLOYEE_BENEFITS_LIMIT_OPTIONS}
                  onChange={(employeeBenefitsEachEmployeeLimit) =>
                    onUpdate({ employeeBenefitsEachEmployeeLimit })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Aggregate Limit"}
                  value={layer?.employeeBenefitsAggregateLimit ?? null}
                  options={EMPLOYEE_BENEFITS_LIMIT_OPTIONS}
                  onChange={(employeeBenefitsAggregateLimit) =>
                    onUpdate({ employeeBenefitsAggregateLimit })
                  }
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Assault or Battery"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Each Event Limit"}
                  value={layer?.assaultBatteryEachEventLimit ?? null}
                  options={ASSAULT_BATTERY_EACH_EVENT_OPTIONS}
                  onChange={(assaultBatteryEachEventLimit) =>
                    onUpdate({ assaultBatteryEachEventLimit })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <LimitSelectField
                  label={"Aggregate Limit"}
                  value={layer?.assaultBatteryAggregateLimit ?? null}
                  options={ASSAULT_BATTERY_AGGREGATE_OPTIONS}
                  onChange={(assaultBatteryAggregateLimit) =>
                    onUpdate({ assaultBatteryAggregateLimit })
                  }
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Premium Adjustments"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Manual Premium Adjustment ($)"}
                  value={layer?.manualPremiumAdjustment ?? null}
                  onChangeNumber={(manualPremiumAdjustment) =>
                    onUpdate({ manualPremiumAdjustment })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"TRIA (%)"}
                  value={
                    layer?.triaPercent != null ? layer.triaPercent * 100 : null
                  }
                  onChangeNumber={(v) =>
                    onUpdate({ triaPercent: v != null ? v / 100 : null })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Commission (%)"}
                  value={
                    layer?.commissionPct != null
                      ? layer.commissionPct * 100
                      : layer?.brokerage ?? null
                  }
                  onChangeNumber={(v) =>
                    onUpdate({ commissionPct: v != null ? v / 100 : null })
                  }
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Loss Rating"}</SubSectionTitle>
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={2}
              sx={{ minHeight: 48 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(applyExperienceMod)}
                    onChange={(e) => onToggleExperienceMod?.(e.target.checked)}
                  />
                }
                label={
                  <Typography fontSize={FONT_SIZE}>
                    {"Apply Experience Mod"}
                  </Typography>
                }
                sx={{ ml: 0, mr: 0 }}
              />
              <Typography fontSize={FONT_SIZE} color={"text.secondary"}>
                {experienceMod != null
                  ? `Experience Mod: ${(experienceMod * 100).toFixed(2)}%${
                      experienceModQualifies
                        ? ""
                        : " (does not qualify — not applied)"
                    }`
                  : "Run ISO Experience Rating (Loss Rating tab) to compute the mod"}
              </Typography>
            </Stack>
          </Stack>

          <IrpmSection
            layer={layer}
            onUpdate={onUpdate}
            open={irpmOpen}
            onToggle={() => setIrpmOpen((v) => !v)}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
};

const GlLimitsInputs = ({ layer, primaryLimits, onUpdate }) => (
  <Grid container spacing={2}>
    <Grid item xs={4}>
      <LimitSelectField
        label={"Occurrence Limit"}
        value={
          layer?.glOccurrenceLimit ?? primaryLimits?.occurrenceLimit ?? null
        }
        options={OCCURRENCE_LIMIT_OPTIONS}
        onChange={(glOccurrenceLimit) => onUpdate({ glOccurrenceLimit })}
      />
    </Grid>
    <Grid item xs={4}>
      <LimitSelectField
        label={"General Aggregate"}
        value={
          layer?.glGeneralAggregate ?? primaryLimits?.generalAggregate ?? null
        }
        options={AGGREGATE_OPTIONS}
        onChange={(glGeneralAggregate) => onUpdate({ glGeneralAggregate })}
      />
    </Grid>
    <Grid item xs={4}>
      <LimitSelectField
        label={"PCO Aggregate"}
        value={layer?.glPcoAggregate ?? primaryLimits?.pcoAggregate ?? null}
        options={AGGREGATE_OPTIONS}
        onChange={(glPcoAggregate) => onUpdate({ glPcoAggregate })}
      />
    </Grid>
    <Grid item xs={4}>
      <LimitSelectField
        label={"Damage to Premises Limit"}
        value={layer?.damageToPremisesLimit ?? null}
        options={DAMAGE_TO_PREMISES_OPTIONS}
        onChange={(damageToPremisesLimit) =>
          onUpdate({ damageToPremisesLimit })
        }
      />
    </Grid>
    <Grid item xs={4}>
      <LimitSelectField
        label={"Medical Expense Limit"}
        value={layer?.medicalExpenseLimit ?? null}
        options={MEDICAL_EXPENSE_OPTIONS}
        onChange={(medicalExpenseLimit) => onUpdate({ medicalExpenseLimit })}
      />
    </Grid>
    <Grid item xs={4}>
      <LimitSelectField
        label={"Personal and Advertising Injury Limit"}
        value={layer?.personalAndAdvertisingInjuryLimit ?? null}
        options={PAI_LIMIT_OPTIONS}
        onChange={(personalAndAdvertisingInjuryLimit) =>
          onUpdate({ personalAndAdvertisingInjuryLimit })
        }
      />
    </Grid>
  </Grid>
);

const GlPricing = () => {
  const dispatch = useDispatch();
  const baseLayerBurn = useSelector(baseLayerBurnStore.selectBaseLayerBurn);
  const activeLayer = useSelector(pricingSelectors.getActiveLayer);
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );

  const {
    data: config,
    isLoading: isConfigLoading,
  } = staticDataService.useConfigQuery("analytics");
  const { data: glConfig } = staticDataService.useConfigQuery("gl_exposures");
  const states = glConfig?.states ?? [];

  const [
    _triggerPricing,
    { isFetching: isPricingFetching },
  ] = analyticsService.useLazySubmissionAnalyticsQuery();

  const handleUpdateLayer = useCallback(
    (values) => {
      if (!activeLayer?.id) return;
      dispatch(updateLayer({ layerId: activeLayer.id, values }));
    },
    [dispatch, activeLayer?.id]
  );

  const triggerPricing = useCallback(async () => {
    if (isConfigLoading) return;
    try {
      const response = await _triggerPricing({
        engine: config?.programPricing?.engine,
        metric: "program_pricing",
        submission,
      });
      dispatch(
        baseLayerBurnStore.update({
          pricingCalculation: { response: response?.data },
        })
      );
      if (!response?.data?.failure) {
        dispatch(programActions.setXsRatingAutoCalculate(true));
        // NAICS Code follows the dominant priced class (see naicsAutoSelect).
        // Derived here, at the moment the result lands, so it is in place
        // whichever tab the underwriter saves from. Inert unless the company's
        // `gl_exposures` config carries the ISO -> NAICS crosswalk, and the
        // reducer never overwrites a NAICS chosen by hand.
        const derivedNaics = naicsAutoSelect.deriveNaics(
          response?.data?.values?.records,
          glConfig?.classCodes
        );
        if (derivedNaics) {
          dispatch(programActions.setDerivedNaics(derivedNaics));
        }
      }
    } catch (err) {
      dispatch(
        baseLayerBurnStore.update({
          pricingCalculation: {
            response: { failure: err?.message ?? "Pricing request failed." },
          },
        })
      );
    }
  }, [
    isConfigLoading,
    config,
    glConfig?.classCodes,
    submission,
    _triggerPricing,
    dispatch,
  ]);

  const lastTotals = baseLayerBurn?.pricing?.values?.totals;
  const glExposures = useSelector(glExposuresStore.selectResolvedGlExposures);
  const primaryGlLimits = useSelector(
    flagsSelectors.selectPrimaryGlRatingLimits
  );
  const experienceRating = useSelector(programSelectors.getExperienceRating);
  const experienceResult = useSelector(
    programSelectors.getExperienceRatingResult
  );
  const onToggleExperienceMod = useCallback(
    (checked) =>
      dispatch(
        programActions.updateExperienceRating("applyExperienceMod", checked)
      ),
    [dispatch]
  );
  const exposurePredominantState = (() => {
    if (!glExposures?.length) return null;
    const counts = {},
      totals = {};
    for (const row of glExposures) {
      if (!row.state) continue;
      counts[row.state] = (counts[row.state] ?? 0) + 1;
      totals[row.state] =
        (totals[row.state] ?? 0) + (parseFloat(row.exposure_amount) || 0);
    }
    let best = null,
      bestCount = 0,
      bestTotal = 0;
    for (const [state, count] of Object.entries(counts)) {
      if (
        count > bestCount ||
        (count === bestCount && totals[state] > bestTotal)
      ) {
        best = state;
        bestCount = count;
        bestTotal = totals[state];
      }
    }
    return best;
  })();

  const hasPricingResult = useRef(false);
  const triggerPricingRef = useRef(triggerPricing);
  triggerPricingRef.current = triggerPricing;
  const autoRecalcDebounce = useRef(null);
  const isFirstAutoRun = useRef(true);

  useEffect(() => {
    if (lastTotals) hasPricingResult.current = true;
  }, [lastTotals]);

  useEffect(() => {
    if (isFirstAutoRun.current) {
      isFirstAutoRun.current = false;
      return;
    }
    if (!hasPricingResult.current || isConfigLoading) return;
    clearTimeout(autoRecalcDebounce.current);
    autoRecalcDebounce.current = setTimeout(
      () => triggerPricingRef.current(),
      800
    );
  }, [
    primaryGlLimits?.occurrenceLimit,
    primaryGlLimits?.generalAggregate,
    primaryGlLimits?.pcoAggregate,
    activeLayer?.glOccurrenceLimit,
    activeLayer?.glGeneralAggregate,
    activeLayer?.glPcoAggregate,
    activeLayer?.damageToPremisesLimit,
    activeLayer?.medicalExpenseLimit,
    activeLayer?.personalAndAdvertisingInjuryLimit,
    activeLayer?.glTerritoryFactors,
    activeLayer?.glPremOpsLossCostOverrides,
    activeLayer?.glPcoLossCostOverrides,
    activeLayer?.liquorOccurrenceLimit,
    activeLayer?.liquorAggregateLimit,
    activeLayer?.deductibleAmount,
    activeLayer?.deductibleType,
    activeLayer?.predominantState,
    activeLayer?.coverageEnhancementsPct,
    activeLayer?.stopGapPayroll,
    activeLayer?.hnoaEmployees,
    activeLayer?.hnoaCostOfHire,
    activeLayer?.commissionPct,
    activeLayer?.brokerage,
    activeLayer?.manualPremiumAdjustment,
    activeLayer?.triaPercent,
    activeLayer?.irpmConditionCare,
    activeLayer?.irpmTypeCondition,
    activeLayer?.irpmSelectionTraining,
    activeLayer?.irpmSafetyProgram,
    activeLayer?.irpmPeculiarities,
    activeLayer?.irpmTermsConditions,
    activeLayer?.irpmUniqueRisk,
    submission?.glLimits?.apply_experience_mod,
    submission?.glLimits?.experience_mod,
    submission?.program?.proRataAdjustment,
    submission?.program?.projectType,
    submission?.program?.insuredInterest,
    isConfigLoading,
  ]);

  return (
    <Disabled ifReadOnly>
      <Component
        title={"GL Pricing"}
        loading={isConfigLoading}
        isFetching={isPricingFetching}
      >
        <Stack direction={"column"} gap={2} sx={{ pr: 2, overflow: "hidden" }}>
          <AdditionalSettings
            layer={activeLayer}
            states={states}
            lastTotals={lastTotals}
            programState={exposurePredominantState}
            applyExperienceMod={experienceRating?.applyExperienceMod ?? false}
            experienceMod={experienceResult?.experience_mod ?? null}
            experienceModQualifies={experienceResult?.qualifies ?? false}
            onToggleExperienceMod={onToggleExperienceMod}
            onUpdate={handleUpdateLayer}
          />
          <GlLimitsInputs
            layer={activeLayer}
            primaryLimits={primaryGlLimits}
            onUpdate={handleUpdateLayer}
          />
          <ProjectWrapInputs />
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            width={"100%"}
            spacing={2}
          >
            {baseLayerBurn?.pricing?.failure && (
              <Alert severity={"error"} sx={{ width: "100%" }}>
                {baseLayerBurn?.pricing?.failure}
              </Alert>
            )}
            <Button
              onClick={triggerPricing}
              isLoading={isPricingFetching}
              disabled={isConfigLoading}
            >
              {"Calculate"}
            </Button>
          </Stack>
          <Divider />
          <GlPricingResults
            values={baseLayerBurn?.pricing?.values}
            territoryFactors={activeLayer?.glTerritoryFactors ?? {}}
            premOpsLcOverrides={activeLayer?.glPremOpsLossCostOverrides ?? {}}
            pcoLcOverrides={activeLayer?.glPcoLossCostOverrides ?? {}}
            onUpdate={handleUpdateLayer}
          />
          <Visible byTag={GL_PRICING_CLASSIFICATIONS} defaultVisible={false}>
            <GLClassificationsTable />
          </Visible>
        </Stack>
      </Component>
    </Disabled>
  );
};

export default GlPricing;
